/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/src/onboarding/telemetry-disclosure.tsx` (verbatim).
 *
 * Zero changes to the body. It had no `"use client"` and no `next/*`, and its only
 * `@excellent/core/server` import is `import type` — erased at compile time, so it
 * is not a graph edge and `check-server-leak` correctly does not flag it. This is
 * the case the leak gate's type-erasure rule exists for.
 *
 * ⚠️ `disclosure` is a prop and stays one. In the Next tree a server component
 * called `packages/core/src/telemetry/disclosure.ts` and passed the result down.
 * There is no mounted Operation that returns a `TelemetryDisclosure`, so whoever
 * ports `/transparency` and Settings ▸ Usage analytics (RO-3-12 / RO-3-09) owes
 * one. The type is the contract's shape already, so it is a thin read endpoint.
 */

import { Badge, Card } from "@excellent/ui";
import type { TelemetryDisclosure, TelemetryCategory } from "@excellent/core/server";

// The "exactly what we collect" panel — a pure rendering of the live telemetry
// disclosure (packages/core/src/telemetry/disclosure.ts), which is itself derived
// from the scrubber's allowlist. Shown on /transparency and in Settings → Usage
// analytics. No client state, no fn-props.

const TYPE_LABEL: Record<string, string> = {
  token: "fixed tag",
  bool: "yes / no",
  count: "a number",
  numeric: "counts only",
};

const CATEGORY_ORDER: TelemetryCategory[] = ["Onboarding funnel", "Feature adoption"];
const CATEGORY_BLURB: Record<TelemetryCategory, string> = {
  "Onboarding funnel": "Where setup stalls — so we can fix the rough edges. Never who you are.",
  "Feature adoption": "Which capabilities get used, as plain counts and flags.",
};

export function TelemetryDisclosurePanel({ disclosure }: { disclosure: TelemetryDisclosure }) {
  const { guarantees, identity, events, neverCollected, meta } = disclosure;

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h2 className="type-title font-medium text-foreground">How this stays anonymous</h2>
        <ul className="mt-3 space-y-2">
          {guarantees.map((g, i) => (
            <li key={i} className="flex gap-2 type-body text-muted-foreground">
              <span aria-hidden className="mt-0.5 text-primary">✓</span>
              <span>{g}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 border-t border-border/40 pt-3 type-subtext text-muted-foreground">
          <span className="font-medium text-foreground">{identity.distinctId}</span> — {identity.note}
        </p>
      </Card>

      <Card className="p-5">
        <h2 className="type-title font-medium text-foreground">
          What we collect{" "}
          <span className="font-normal text-muted-foreground">· {meta.eventCount} events, all anonymous</span>
        </h2>
        <div className="mt-2">
          {CATEGORY_ORDER.map((cat) => {
            const inCat = events.filter((e) => e.category === cat);
            if (inCat.length === 0) return null;
            return (
              <div key={cat} className="mt-4 first:mt-2">
                <p className="type-subtext font-medium uppercase tracking-wide text-muted-foreground">{cat}</p>
                <p className="type-subtext text-muted-foreground/80">{CATEGORY_BLURB[cat]}</p>
                <ul className="mt-2">
                  {inCat.map((e) => (
                    <li key={e.name} className="border-b border-border/40 py-2.5 first:pt-1 last:border-b-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="type-body font-medium text-foreground">{e.title}</span>
                        <code className="type-subtext text-muted-foreground">{e.name}</code>
                        {e.when ? <span className="type-subtext text-muted-foreground">— when {e.when}</span> : null}
                      </div>
                      {e.fields.length > 0 ? (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {e.fields.map((f) => (
                            <Badge
                              key={f.key}
                              variant="secondary"
                              title={f.meaning}
                            >
                              <span className="font-medium text-foreground">{f.key}</span>
                              <span className="text-muted-foreground/70">{TYPE_LABEL[f.type] ?? f.type}</span>
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-1 type-subtext text-muted-foreground/70">No fields — just that it happened.</p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="type-title font-medium text-foreground">What never leaves your device</h2>
        <p className="mt-1 type-subtext text-muted-foreground">
          These are blocked at the source regardless of any setting — an absolute denylist, on top of the allowlist above.
        </p>
        <ul className="mt-3 grid gap-3 sm:grid-cols-2">
          {neverCollected.map((g) => (
            <li key={g.group} className="rounded-md border border-border/50 p-3">
              <p className="type-body font-medium text-foreground">{g.group}</p>
              <p className="type-subtext text-muted-foreground">{g.note}</p>
              {g.examples.length > 0 ? (
                <p className="mt-1.5 type-subtext text-muted-foreground/70">
                  e.g. {g.examples.map((x) => x).join(", ")}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>

      <p className="px-1 type-subtext text-muted-foreground">
        This page is generated live from <code>{meta.derivedFrom}</code> — the same code that runs before anything
        is sent. The pipeline is open source and the receiving sink re-checks every event against this exact list.
      </p>
    </div>
  );
}
